/**
 * A dropdown of the portal's own (Mohit, 2 October 2026). A native <select>
 * opens a list the operating system draws, rounded and translucent on a Mac,
 * which no stylesheet can reach, so it could never match the rest of
 * the portal. This is the select-only combobox of the ARIA Authoring
 * Practices: a button that names the choice, and a list under it.
 *
 *   closed   Enter, Space, Down or Up opens it; Home and End open it at the
 *            first or last; a letter opens it at the next choice starting so
 *   open     Up and Down move, Home and End jump, a letter jumps, Enter or
 *            Space chooses and closes, Tab chooses and moves on, Escape
 *            closes without choosing, a click outside closes
 *
 * Focus never leaves the button: the list is pointed at with
 * aria-activedescendant, so a <label for> names it and a screen reader reads
 * the option under the cursor. The list is always in the page, hidden while
 * closed, so what it offers can be read without opening it.
 */

import { useEffect, useRef, useState } from "preact/hooks";

export interface SelectOption {
  readonly value: string;
  readonly label: string;
}

export function Select(props: {
  id: string;
  value: string;
  options: readonly SelectOption[];
  onChange: (value: string) => void;
  /** Shown while value is "" and nothing is chosen yet. */
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  describedBy?: string;
  /** sm: the height of a small button, for a toolbar (29 October 2026). */
  size?: "sm";
  /** The list opens above the button, for a dropdown at the foot of the screen (the account menu's theme). */
  up?: boolean;
}) {
  const { id, value, options } = props;
  const [open, setOpen] = useState(false);
  const chosenAt = options.findIndex((option) => option.value === value);
  const [active, setActive] = useState(Math.max(0, chosenAt));
  const wrap = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLUListElement>(null);

  // A click anywhere else closes the list.
  useEffect(() => {
    if (!open) return;
    const away = (event: MouseEvent) => {
      if (!wrap.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);

  // The option under the cursor stays in view in a long list.
  useEffect(() => {
    if (open) list.current?.children[active]?.scrollIntoView({ block: "nearest" });
  }, [open, active]);

  const show = (at: number) => {
    setActive(Math.min(Math.max(at, 0), options.length - 1));
    setOpen(true);
  };
  const choose = (at: number) => {
    const option = options[at];
    setOpen(false);
    if (option && option.value !== value) props.onChange(option.value);
  };
  const startingWith = (key: string, from: number) => {
    const k = key.toLowerCase();
    for (let step = 1; step <= options.length; step += 1) {
      const at = (from + step) % options.length;
      if (options[at]?.label.toLowerCase().startsWith(k)) return at;
    }
    return -1;
  };

  const onKeyDown = (event: KeyboardEvent) => {
    const here = open ? active : Math.max(0, chosenAt);
    let handled = true;
    if (event.key === "ArrowDown") open ? setActive(Math.min(active + 1, options.length - 1)) : show(here);
    else if (event.key === "ArrowUp") open ? setActive(Math.max(active - 1, 0)) : show(here);
    else if (event.key === "Home") show(0);
    else if (event.key === "End") show(options.length - 1);
    else if (event.key === "Enter" || event.key === " ") open ? choose(active) : show(here);
    else if (event.key === "Escape" && open) setOpen(false);
    else if (event.key === "Tab") {
      if (open) choose(active);
      handled = false;
    } else if (event.key.length === 1 && /\S/.test(event.key)) {
      const at = startingWith(event.key, here);
      if (at >= 0) show(at);
    } else handled = false;
    if (handled) event.preventDefault();
  };

  const chosen = options[chosenAt];
  return (
    <div class={["select", props.size === "sm" ? "sm" : "", props.up ? "up" : "", open ? "is-open" : ""].filter(Boolean).join(" ")} ref={wrap}>
      <button
        type="button"
        id={id}
        class="input select-button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={`${id}-list`}
        aria-activedescendant={open ? `${id}-option-${active}` : undefined}
        aria-invalid={props.invalid ? "true" : undefined}
        aria-describedby={props.describedBy}
        data-value={value}
        disabled={props.disabled}
        onClick={() => (open ? setOpen(false) : show(Math.max(0, chosenAt)))}
        onKeyDown={onKeyDown}
      >
        <span class={chosen ? "select-value" : "select-value select-placeholder"}>{chosen ? chosen.label : (props.placeholder ?? "")}</span>
      </button>
      <ul id={`${id}-list`} class="select-list" role="listbox" aria-labelledby={id} hidden={!open} ref={list}>
        {options.map((option, at) => (
          <li
            key={option.value}
            id={`${id}-option-${at}`}
            role="option"
            class={at === active ? "select-option is-active" : "select-option"}
            aria-selected={option.value === value}
            data-value={option.value}
            // Before the button loses focus, so the click lands.
            onMouseDown={(event) => event.preventDefault()}
            onMouseEnter={() => setActive(at)}
            onClick={() => choose(at)}
          >
            {option.label}
          </li>
        ))}
      </ul>
    </div>
  );
}
